import { MASKED_PLACEHOLDER } from '@/sections/settings/constants';
import { Account } from '@/types';

import {
  NotificationField,
  NotificationViewModal,
} from './NotificationViewModal';

interface WebhookViewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The webhook account to view/edit */
  webhook: Account;
}

/**
 * WebhookViewModal - Modal for viewing webhook configuration and editing severity threshold
 *
 * Features:
 * - Displays masked placeholders for webhook URL, auth header, and auth token
 * - Values are stored in AWS Secrets Manager and cannot be retrieved
 * - Editable severity threshold dropdown
 * - Only severity can be edited (backend constraint)
 *
 * Note: Webhook URL, auth header, and auth token are displayed as masked placeholders
 * because they are stored in AWS Secrets Manager and cannot be retrieved after creation.
 *
 * Used in:
 * - NotificationSettingsCards via OutgoingWebhookSection
 */
export function WebhookViewModal({
  open,
  onClose,
  webhook,
}: WebhookViewModalProps) {
  const fields: NotificationField[] = [
    {
      label: 'Webhook URL',
      value: MASKED_PLACEHOLDER,
      type: 'masked',
      tooltip:
        'Webhook URL is stored in AWS Secrets Manager and cannot be viewed after creation for security reasons',
      testId: 'webhook-view-url',
    },
    {
      label: 'Authorization Header',
      value: MASKED_PLACEHOLDER,
      type: 'masked',
      tooltip:
        'Authorization header is stored in AWS Secrets Manager and is immutable for security reasons',
      testId: 'webhook-view-header',
    },
    {
      label: 'Authorization Token',
      value: MASKED_PLACEHOLDER,
      type: 'masked',
      tooltip:
        'Authorization token is stored in AWS Secrets Manager and is immutable for security reasons',
      testId: 'webhook-view-token',
    },
  ];

  return (
    <NotificationViewModal
      open={open}
      onClose={onClose}
      account={webhook}
      title="Webhook Configuration"
      username="webhook"
      fields={fields}
      successMessage="Webhook updated"
      errorMessage="Failed to update webhook"
      modalTestId="webhook-view-modal"
      saveButtonTestId="webhook-view-save-button"
      severitySelectTestId="webhook-view-severity-select"
      severitySelectName="webhook-severity"
    />
  );
}
